import {
  Add01Icon,
  Delete01Icon,
  Edit01Icon,
  File01Icon,
  FolderOpenIcon,
  Image01Icon,
  Loading01Icon,
  MoreVerticalIcon,
  Search01Icon,
  Upload01Icon,
  ViewIcon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useSearch } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { KnowledgeDoc, KnowledgeFolderTreeNode } from 'shared';
import { KNOWLEDGE_PERMISSIONS } from 'shared';
import { toast } from 'sonner';
import { getKnowledgeDocs } from '@/components/features/knowledge/api/knowledge-docs';
import { PermissionGuard } from '@/components/shared/PermissionGuard';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { EllipsisText } from '@/components/ui/ellipsis-text';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TooltipProvider } from '@/components/ui/tooltip';
import { UserAvatar } from '@/components/ui/user-avatar';
import { usePermissions } from '@/hooks/usePermissions';
import { knowledgeDocsApi, knowledgeFoldersApi } from '@/lib/api-client';
import { getTenantId } from '@/lib/better-auth';

// Extended interface for API-expanded fields
interface KnowledgeDocWithDetails extends KnowledgeDoc {
  createdByName?: string | null;
  createdByProfilePictureUrl?: string | null;
  updatedByName?: string | null;
  updatedByProfilePictureUrl?: string | null;
}

import { ChevronRight, Folder01Icon } from '@hugeicons/core-free-icons';
import Image from '@tiptap/extension-image';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { FolderFormDialog } from '@/components/features/knowledge/FolderFormDialog';
import { FolderTree } from '@/components/features/knowledge/FolderTree';
import { ImageAnnotator } from '@/components/features/knowledge/ImageAnnotator';
import { RichTextEditor } from '@/components/features/knowledge/RichTextEditor';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

// Folder selector component for the move dialog
interface FolderSelectorProps {
  folders: KnowledgeFolderTreeNode[];
  selectedFolderId: string | null;
  onFolderSelect: (folderId: string | null) => void;
  currentDocFolderId: string | null;
  level?: number;
  t: (key: string) => string;
}

function FolderSelector({
  folders,
  selectedFolderId,
  onFolderSelect,
  currentDocFolderId,
  level = 0,
  t,
}: FolderSelectorProps) {
  return (
    <>
      {/* Root / No Folder option */}
      {level === 0 && (
        <button
          onClick={() => onFolderSelect(null)}
          className={cn(
            'w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-muted/50 transition-colors',
            currentDocFolderId === null &&
              'text-muted-foreground cursor-not-allowed hover:bg-transparent',
            currentDocFolderId !== null && 'hover:underline',
          )}
          disabled={currentDocFolderId === null}
          title={currentDocFolderId === null ? t('moveDialog.currentLocation') : undefined}
        >
          <HugeiconsIcon icon={Folder01Icon} className="h-4 w-4 shrink-0" />
          <span className="flex-1">{t('moveDialog.noFolder')}</span>
          {currentDocFolderId === null && (
            <span className="text-xs text-muted-foreground">({t('moveDialog.current')})</span>
          )}
        </button>
      )}
      {folders.map((folder) => (
        <div key={folder.id}>
          <button
            onClick={() => {
              if (folder.id !== currentDocFolderId) {
                onFolderSelect(folder.id);
              }
            }}
            className={cn(
              'w-full flex items-center gap-1 px-3 py-2 text-sm text-left hover:bg-muted/50 transition-colors',
              folder.id === currentDocFolderId &&
                'text-muted-foreground cursor-not-allowed hover:bg-transparent',
              folder.id !== currentDocFolderId && 'hover:underline',
            )}
            disabled={folder.id === currentDocFolderId}
            style={{ paddingLeft: `${level * 16 + 12}px` }}
            title={folder.id === currentDocFolderId ? t('moveDialog.currentLocation') : undefined}
          >
            <HugeiconsIcon icon={ChevronRight} className="h-3 w-3 shrink-0" />
            <HugeiconsIcon icon={Folder01Icon} className="h-4 w-4 shrink-0 text-blue-500" />
            <span className="flex-1 truncate">{folder.name}</span>
            {folder.id === currentDocFolderId && (
              <span className="text-xs text-muted-foreground">({t('moveDialog.current')})</span>
            )}
          </button>
          {folder.children.length > 0 && (
            <FolderSelector
              folders={folder.children}
              selectedFolderId={selectedFolderId}
              onFolderSelect={onFolderSelect}
              currentDocFolderId={currentDocFolderId}
              level={level + 1}
              t={t}
            />
          )}
        </div>
      ))}
    </>
  );
}

import { MobileKnowledgeCenter } from '@/components/features/knowledge/MobileKnowledgeCenter';

function KnowledgeCenter() {
  return (
    <>
      <div className="hidden md:block">
        <DesktopKnowledgeCenter />
      </div>
      <div className="md:hidden">
        <MobileKnowledgeCenter />
      </div>
    </>
  );
}

function DesktopKnowledgeCenter() {
  const { t } = useTranslation('knowledge');
  const { docId } = useSearch({ strict: false });
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const _canCreate = hasPermission(KNOWLEDGE_PERMISSIONS.CREATE);
  const _canUpdate = hasPermission(KNOWLEDGE_PERMISSIONS.UPDATE);
  const _canDelete = hasPermission(KNOWLEDGE_PERMISSIONS.DELETE);
  const [search, setSearch] = useState('');
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);

  // Folder dialog state
  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const [editingFolder, setEditingFolder] = useState<KnowledgeFolderTreeNode | null>(null);
  const [parentForNewFolder, setParentForNewFolder] = useState<string | null>(null);

  // Upload dialog state
  const [uploadOpen, setUploadOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [fileTitle, setFileTitle] = useState('');
  const [fileDescription, setFileDescription] = useState('');

  // Write/Edit dialog state
  const [writeOpen, setWriteOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState<KnowledgeDoc | null>(null);
  const [docTitle, setDocTitle] = useState('');
  const [docDescription, setDocDescription] = useState('');
  const [docContent, setDocContent] = useState('');

  // View dialog state
  const [viewOpen, setViewOpen] = useState(false);
  const [viewingDoc, setViewingDoc] = useState<KnowledgeDoc | null>(null);

  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [docToDelete, setDocToDelete] = useState<KnowledgeDoc | null>(null);

  // Move dialog state
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [docToMove, setDocToMove] = useState<KnowledgeDoc | null>(null);

  // Handler functions (defined before queries/effects that use them)
  const handleViewOpen = (doc: KnowledgeDoc) => {
    setViewingDoc(doc);
    setViewOpen(true);
  };

  const docsQuery = useQuery({
    queryKey: ['knowledge-docs', { search, folderId: selectedFolderId }],
    queryFn: () => getKnowledgeDocs({ search, folderId: selectedFolderId ?? undefined }),
  });

  // Folder tree query
  const foldersQuery = useQuery({
    queryKey: ['knowledge-folders-tree'],
    queryFn: () => knowledgeFoldersApi.getTree(),
  });

  // Delete folder mutation
  const deleteFolderMutation = useMutation({
    mutationFn: (id: string) => knowledgeFoldersApi.delete(id),
    onSuccess: () => {
      toast.success(t('folderDialog.deleted'));
      queryClient.invalidateQueries({ queryKey: ['knowledge-folders-tree'] });
      queryClient.invalidateQueries({ queryKey: ['knowledge-docs'] });
    },
    onError: (error: Error) => {
      toast.error(error?.message || t('folderDialog.deleteError'));
    },
  });

  // Auto-open doc when docId is in URL
  useEffect(() => {
    if (docId && !viewOpen) {
      // First try to find the doc in the currently loaded list
      if (docsQuery.data?.data) {
        const doc = docsQuery.data.data.find((d) => d.id === docId);
        if (doc) {
          handleViewOpen(doc);
          return;
        }
      }
      // If not found in list (pagination/search), create minimal doc and open
      // The viewingDocQuery will fetch full details
      handleViewOpen({ id: docId } as KnowledgeDoc);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    docId,
    docsQuery.data?.data?.find, // If not found in list (pagination/search), create minimal doc and open
    // The viewingDocQuery will fetch full details
    handleViewOpen,
    viewOpen,
    docsQuery.data?.data,
  ]);

  const uploadMutation = useMutation({
    mutationFn: (payload: {
      file: File;
      title?: string;
      description?: string | null;
      folderId?: string | null;
    }) => knowledgeDocsApi.uploadFile(payload),
    onSuccess: () => {
      toast.success(t('messages.fileUploaded'));
      setUploadOpen(false);
      resetUploadForm();
      queryClient.invalidateQueries({ queryKey: ['knowledge-docs'] });
      queryClient.invalidateQueries({ queryKey: ['knowledge-folders-tree'] });
    },
    onError: (error: Error) => {
      toast.error(error?.message || t('messages.uploadFailed'));
    },
  });

  const createDocMutation = useMutation({
    mutationFn: (payload: {
      title: string;
      description?: string | null;
      content: string;
      folderId?: string | null;
    }) =>
      knowledgeDocsApi.createRichText({
        title: payload.title,
        description: payload.description,
        type: 'rich-text',
        content: payload.content,
        folderId: payload.folderId,
      }),
    onSuccess: () => {
      toast.success(t('messages.documentCreated'));
      setWriteOpen(false);
      resetWriteForm();
      queryClient.invalidateQueries({ queryKey: ['knowledge-docs'] });
      queryClient.invalidateQueries({ queryKey: ['knowledge-folders-tree'] });
    },
    onError: (error: Error) => {
      toast.error(error?.message || t('messages.createFailed'));
    },
  });

  const updateDocMutation = useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: {
        title: string;
        description?: string | null;
        content: string;
        folderId?: string | null;
      };
    }) =>
      knowledgeDocsApi.updateRichText(id, {
        title: payload.title,
        description: payload.description,
        type: 'rich-text',
        content: payload.content,
        folderId: payload.folderId,
      }),
    onSuccess: (_, variables) => {
      toast.success(t('messages.documentUpdated'));
      setWriteOpen(false);
      resetWriteForm();
      queryClient.invalidateQueries({ queryKey: ['knowledge-docs'] });
      queryClient.invalidateQueries({ queryKey: ['knowledge-doc', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['knowledge-folders-tree'] });
    },
    onError: (error: Error) => {
      toast.error(error?.message || t('messages.updateFailed'));
    },
  });

  const docQuery = useQuery({
    queryKey: ['knowledge-doc', editingDoc?.id],
    queryFn: () => knowledgeDocsApi.getById(editingDoc!.id),
    enabled: !!editingDoc?.id && writeOpen && editingDoc.type === 'rich-text',
  });

  const resetUploadForm = () => {
    setFile(null);
    setFileTitle('');
    setFileDescription('');
  };

  const resetWriteForm = () => {
    setEditingDoc(null);
    setDocTitle('');
    setDocDescription('');
    setDocContent('');
  };

  const handleFileUpload = () => {
    if (!file) {
      toast.error(t('messages.chooseFile'));
      return;
    }
    uploadMutation.mutate({
      file,
      title: fileTitle || undefined,
      description: fileDescription || undefined,
      folderId: selectedFolderId,
    });
  };

  const handleCreateDoc = () => {
    if (!docTitle.trim()) {
      toast.error(t('messages.titleRequired'));
      return;
    }
    if (!docContent.trim()) {
      toast.error(t('messages.contentRequired'));
      return;
    }
    createDocMutation.mutate({
      title: docTitle.trim(),
      description: docDescription || undefined,
      content: docContent,
      folderId: selectedFolderId,
    });
  };

  const handleUpdateDoc = () => {
    if (!editingDoc) return;
    if (!docTitle.trim()) {
      toast.error(t('messages.titleRequired'));
      return;
    }
    if (!docContent.trim()) {
      toast.error(t('messages.contentRequired'));
      return;
    }
    updateDocMutation.mutate({
      id: editingDoc.id,
      payload: {
        title: docTitle.trim(),
        description: docDescription || undefined,
        content: docContent,
        folderId: selectedFolderId,
      },
    });
  };

  const handleWriteOpen = (doc?: KnowledgeDoc) => {
    if (doc && doc.type === 'rich-text') {
      setEditingDoc(doc);
      // Title and description can be set immediately, content will be loaded via query
      setDocTitle(doc.title);
      setDocDescription(doc.description || '');
      setDocContent('');
    } else {
      resetWriteForm();
    }
    setWriteOpen(true);
  };

  // Fetch full document details when viewing (includes content)
  const viewingDocQuery = useQuery({
    queryKey: ['knowledge-doc', viewingDoc?.id],
    queryFn: () => knowledgeDocsApi.getById(viewingDoc!.id),
    enabled: !!viewingDoc?.id && viewOpen,
  });

  const deleteDocMutation = useMutation({
    mutationFn: (id: string) => knowledgeDocsApi.delete(id),
    onSuccess: () => {
      toast.success(t('messages.documentDeleted'));
      queryClient.invalidateQueries({ queryKey: ['knowledge-docs'] });
      queryClient.invalidateQueries({ queryKey: ['knowledge-folders-tree'] });
    },
    onError: (error: Error) => {
      // @ts-expect-error - API error shape extends base Error
      const errorData = error?.data || error?.error || error;
      if (
        errorData?.code === 'FOREIGN_KEY_CONSTRAINT' ||
        errorData?.constraint === 'foreign_key' ||
        errorData?.error === 'Cannot delete'
      ) {
        toast.error(errorData.error || t('deleteDialog.cannotDelete'), {
          description: errorData.message || t('deleteDialog.cannotDeleteMessage'),
          duration: 7000,
        });
      } else {
        toast.error(errorData?.message || error?.message || t('messages.deleteFailed'));
      }
    },
  });

  const moveDocMutation = useMutation({
    mutationFn: ({ id, targetFolderId }: { id: string; targetFolderId: string | null }) =>
      knowledgeDocsApi.moveDocument(id, targetFolderId),
    onSuccess: () => {
      toast.success(t('messages.documentMoved'));
      setMoveDialogOpen(false);
      setDocToMove(null);
      queryClient.invalidateQueries({ queryKey: ['knowledge-docs'] });
      queryClient.invalidateQueries({ queryKey: ['knowledge-folders-tree'] });
    },
    onError: (error: Error) => {
      toast.error(error?.message || t('messages.moveFailed'));
    },
  });

  // Load doc content when editing
  useEffect(() => {
    if (writeOpen && editingDoc && editingDoc.type === 'rich-text' && docQuery.data) {
      setDocTitle(docQuery.data.title);
      setDocDescription(docQuery.data.description || '');
      setDocContent(docQuery.data.content || '');
    }
  }, [writeOpen, editingDoc, docQuery.data]);

  const docs = docsQuery.data?.data ?? [];

  const renderType = (doc: KnowledgeDoc) => {
    if (doc.type === 'pdf')
      return (
        <Badge variant="outline" className="gap-1">
          <HugeiconsIcon icon={File01Icon} className="h-3 w-3" /> {t('types.pdf')}
        </Badge>
      );
    if (doc.type === 'image')
      return (
        <Badge variant="outline" className="gap-1">
          <HugeiconsIcon icon={Image01Icon} className="h-3 w-3" /> {t('types.image')}
        </Badge>
      );
    return <Badge variant="outline">{t('types.richText')}</Badge>;
  };

  const getFileUrl = (r2Key: string | null | undefined) => {
    if (!r2Key) return null;
    const tenantId = getTenantId();
    if (!tenantId) {
      // Fallback for development or when tenant is not set
      return `https://r2-checklist.opexmx.com/${r2Key}`;
    }
    return `https://r2-${tenantId}.opexmx.com/${r2Key}`;
  };

  return (
    <div className="flex flex-col gap-4 sm:gap-6 h-[calc(100vh-8rem)]">
      <div className="flex flex-col gap-2">
        <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-balance">{t('title')}</h1>
        <p className="text-xs sm:text-sm text-muted-foreground">{t('description')}</p>
      </div>

      {/* Two-panel layout with folder tree and document browser */}
      <div className="flex gap-4 flex-1 min-h-0">
        {/* Left sidebar - Folder tree */}
        <div className="w-64 border rounded-lg flex flex-col shrink-0">
          {foldersQuery.isLoading ? (
            <div className="flex items-center justify-center p-4">
              <HugeiconsIcon
                icon={Loading01Icon}
                className="h-5 w-5 animate-spin text-muted-foreground"
              />
            </div>
          ) : (
            <FolderTree
              folders={foldersQuery.data ?? []}
              selectedFolderId={selectedFolderId}
              onFolderSelect={setSelectedFolderId}
              onCreateFolder={(parentId) => {
                setEditingFolder(null);
                setParentForNewFolder(parentId);
                setFolderDialogOpen(true);
              }}
              onEditFolder={(folder) => {
                setEditingFolder(folder);
                setParentForNewFolder(null);
                setFolderDialogOpen(true);
              }}
              onDeleteFolder={(folderId) => {
                if (confirm(t('folderDialog.confirmDelete'))) {
                  deleteFolderMutation.mutate(folderId);
                }
              }}
            />
          )}
        </div>

        {/* Right content - Documents list with actions */}
        <div className="flex-1 flex flex-col min-w-0">
          <Card className="flex-1 flex flex-col">
            <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between p-3 sm:p-6">
              <div>
                <CardTitle className="text-base sm:text-lg">
                  {selectedFolderId
                    ? foldersQuery.data?.find((f) => f.id === selectedFolderId)?.name ||
                      t('library.title')
                    : t('library.title')}
                </CardTitle>
                <CardDescription className="text-xs sm:text-sm">
                  {t('library.description')}
                </CardDescription>
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
                <div className="flex items-center gap-2 flex-1 sm:flex-initial">
                  <HugeiconsIcon
                    icon={Search01Icon}
                    className="h-4 w-4 text-muted-foreground shrink-0"
                  />
                  <Input
                    placeholder={t('search.placeholder')}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full sm:w-56"
                  />
                </div>
                <TooltipProvider>
                  <PermissionGuard
                    permission={KNOWLEDGE_PERMISSIONS.CREATE}
                    showTooltip
                    tooltipMessage="You don't have permission to create knowledge docs"
                  >
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setUploadOpen(true)}
                      className="gap-2 w-full sm:w-auto"
                    >
                      <HugeiconsIcon icon={Upload01Icon} className="h-4 w-4" />
                      <span className="hidden sm:inline">{t('buttons.uploadFile')}</span>
                      <span className="sm:hidden">{t('buttons.upload')}</span>
                    </Button>
                  </PermissionGuard>
                  <PermissionGuard
                    permission={KNOWLEDGE_PERMISSIONS.CREATE}
                    showTooltip
                    tooltipMessage="You don't have permission to create knowledge docs"
                  >
                    <Button
                      size="sm"
                      onClick={() => handleWriteOpen()}
                      className="gap-2 w-full sm:w-auto"
                    >
                      <HugeiconsIcon icon={Add01Icon} className="h-4 w-4" />
                      <span className="hidden sm:inline">{t('buttons.writeDocument')}</span>
                      <span className="sm:hidden">{t('buttons.write')}</span>
                    </Button>
                  </PermissionGuard>
                </TooltipProvider>
              </div>
            </CardHeader>
            <CardContent className="p-3 sm:p-6 pt-0 overflow-x-auto">
              {docsQuery.isLoading ? (
                <div className="py-6 text-center text-xs sm:text-sm text-muted-foreground">
                  {t('messages.loading')}
                </div>
              ) : docs.length === 0 ? (
                <div className="py-6 text-center text-xs sm:text-sm text-muted-foreground">
                  {t('messages.noDocuments')}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs sm:text-sm">
                        {t('table.headers.title')}
                      </TableHead>
                      <TableHead className="text-xs sm:text-sm">
                        {t('table.headers.type')}
                      </TableHead>
                      <TableHead className="text-xs sm:text-sm hidden md:table-cell">
                        {t('table.headers.description')}
                      </TableHead>
                      <TableHead className="text-xs sm:text-sm w-[100px] sm:w-[140px]">
                        {t('table.headers.size')}
                      </TableHead>
                      <TableHead className="text-xs sm:text-sm w-[100px] sm:w-[120px]">
                        {t('table.headers.createdAt')}
                      </TableHead>
                      <TableHead className="text-xs sm:text-sm w-[100px] sm:w-[120px]">
                        {t('table.headers.updatedAt')}
                      </TableHead>
                      <TableHead className="text-xs sm:text-sm w-[100px] sm:w-[120px]">
                        {t('table.headers.actions')}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {docs.map((doc) => (
                      <TableRow key={doc.id}>
                        <TableCell className="font-medium text-xs sm:text-sm">
                          <EllipsisText maxWidth="200px">{doc.title}</EllipsisText>
                        </TableCell>
                        <TableCell className="text-xs sm:text-sm">{renderType(doc)}</TableCell>
                        <TableCell className="text-muted-foreground text-xs sm:text-sm hidden md:table-cell">
                          <EllipsisText maxWidth="250px">{doc.description || '-'}</EllipsisText>
                        </TableCell>
                        <TableCell className="text-xs sm:text-sm text-muted-foreground">
                          {doc.size ? `${(doc.size / 1024).toFixed(1)} KB` : '—'}
                        </TableCell>
                        <TableCell className="text-xs sm:text-sm text-muted-foreground">
                          {doc.createdAt ? (
                            <div className="flex flex-col">
                              <span>{new Date(doc.createdAt).toLocaleDateString()}</span>
                              {(doc as KnowledgeDocWithDetails).createdByName ||
                              (doc as KnowledgeDocWithDetails).createdBy ? (
                                <div className="mt-1">
                                  <UserAvatar
                                    userName={(doc as KnowledgeDocWithDetails).createdByName}
                                    userId={(doc as KnowledgeDocWithDetails).createdBy}
                                    userProfilePictureUrl={
                                      (doc as KnowledgeDocWithDetails).createdByProfilePictureUrl
                                    }
                                    size="h-5 w-5"
                                    className="text-xs"
                                  />
                                </div>
                              ) : null}
                            </div>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell className="text-xs sm:text-sm text-muted-foreground">
                          {doc.updatedAt ? (
                            <div className="flex flex-col">
                              <span>{new Date(doc.updatedAt).toLocaleDateString()}</span>
                              {(doc as KnowledgeDocWithDetails).updatedByName ||
                              (doc as KnowledgeDocWithDetails).updatedBy ? (
                                <div className="mt-1">
                                  <UserAvatar
                                    userName={(doc as KnowledgeDocWithDetails).updatedByName}
                                    userId={(doc as KnowledgeDocWithDetails).updatedBy}
                                    userProfilePictureUrl={
                                      (doc as KnowledgeDocWithDetails).updatedByProfilePictureUrl
                                    }
                                    size="h-5 w-5"
                                    className="text-xs"
                                  />
                                </div>
                              ) : null}
                            </div>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 sm:h-8 sm:w-8 p-0"
                              >
                                <HugeiconsIcon
                                  icon={MoreVerticalIcon}
                                  className="h-3.5 w-3.5 sm:h-4 sm:w-4"
                                />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent align="end" className="w-40 p-1">
                              <div className="flex flex-col gap-0.5">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="justify-start h-8 px-2 gap-2"
                                  onClick={() => handleViewOpen(doc)}
                                >
                                  <HugeiconsIcon icon={ViewIcon} className="h-4 w-4" />
                                  {t('table.actions.view')}
                                </Button>
                                {doc.type === 'rich-text' && (
                                  <TooltipProvider>
                                    <PermissionGuard
                                      permission={KNOWLEDGE_PERMISSIONS.UPDATE}
                                      showTooltip
                                      tooltipMessage="You don't have permission to edit knowledge docs"
                                    >
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="justify-start h-8 px-2 gap-2"
                                        onClick={() => handleWriteOpen(doc)}
                                      >
                                        <HugeiconsIcon icon={Edit01Icon} className="h-4 w-4" />
                                        {t('table.actions.edit')}
                                      </Button>
                                    </PermissionGuard>
                                  </TooltipProvider>
                                )}
                                <TooltipProvider>
                                  <PermissionGuard
                                    permission={KNOWLEDGE_PERMISSIONS.UPDATE}
                                    showTooltip
                                    tooltipMessage="You don't have permission to edit knowledge docs"
                                  >
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="justify-start h-8 px-2 gap-2"
                                      onClick={() => {
                                        setDocToMove(doc);
                                        setMoveDialogOpen(true);
                                      }}
                                    >
                                      <HugeiconsIcon icon={FolderOpenIcon} className="h-4 w-4" />
                                      {t('table.actions.move')}
                                    </Button>
                                  </PermissionGuard>
                                </TooltipProvider>
                                <TooltipProvider>
                                  <PermissionGuard
                                    permission={KNOWLEDGE_PERMISSIONS.DELETE}
                                    showTooltip
                                    tooltipMessage="You don't have permission to delete knowledge docs"
                                  >
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="justify-start h-8 px-2 gap-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                                      onClick={() => {
                                        setDocToDelete(doc);
                                        setDeleteDialogOpen(true);
                                      }}
                                    >
                                      <HugeiconsIcon icon={Delete01Icon} className="h-4 w-4" />
                                      {t('table.actions.delete')}
                                    </Button>
                                  </PermissionGuard>
                                </TooltipProvider>
                              </div>
                            </PopoverContent>
                          </Popover>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Folder Form Dialog */}
      <FolderFormDialog
        open={folderDialogOpen}
        onOpenChange={setFolderDialogOpen}
        folder={editingFolder}
        parentFolderId={parentForNewFolder}
      />

      {/* Upload Dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('uploadDialog.title')}</DialogTitle>
            <DialogDescription>{t('uploadDialog.description')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="flex flex-col gap-3 sm:flex-row">
              <Input
                type="file"
                accept="application/pdf,image/png,image/jpeg,image/webp"
                onChange={(e) => {
                  const f = e.target.files?.[0] ?? null;
                  setFile(f);
                  if (f && !fileTitle) {
                    setFileTitle(f.name.replace(/\.[^/.]+$/, ''));
                  }
                }}
              />
            </div>
            <ImageAnnotator file={file} onChange={(annotated) => setFile(annotated)} />
            <div className="grid gap-3 grid-cols-1 sm:grid-cols-2">
              <div className="space-y-1.5">
                <label className="text-xs sm:text-sm font-medium">
                  {t('uploadDialog.fields.title')}
                </label>
                <Input
                  value={fileTitle}
                  onChange={(e) => setFileTitle(e.target.value)}
                  placeholder={t('uploadDialog.fields.titlePlaceholder')}
                  className="text-xs sm:text-sm"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs sm:text-sm font-medium">
                  {t('uploadDialog.fields.description')}
                </label>
                <Input
                  value={fileDescription}
                  onChange={(e) => setFileDescription(e.target.value)}
                  placeholder={t('uploadDialog.fields.descriptionPlaceholder')}
                  className="text-xs sm:text-sm"
                />
              </div>
            </div>
            <div className="flex flex-col sm:flex-row justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setUploadOpen(false);
                  resetUploadForm();
                }}
                className="w-full sm:w-auto text-xs sm:text-sm"
              >
                {t('buttons.cancel')}
              </Button>
              <Button
                onClick={handleFileUpload}
                disabled={uploadMutation.isPending}
                className="gap-2 w-full sm:w-auto text-xs sm:text-sm"
              >
                <HugeiconsIcon icon={Upload01Icon} className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                {uploadMutation.isPending ? t('uploadDialog.uploading') : t('buttons.upload')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Write/Edit Dialog */}
      <Dialog
        open={writeOpen}
        onOpenChange={(open) => {
          setWriteOpen(open);
          if (!open) {
            resetWriteForm();
          }
        }}
      >
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingDoc ? t('writeDialog.editTitle') : t('writeDialog.createTitle')}
            </DialogTitle>
            <DialogDescription>{t('writeDialog.description')}</DialogDescription>
          </DialogHeader>
          {docQuery.isLoading && editingDoc ? (
            <div className="py-6 text-center text-muted-foreground">{t('writeDialog.loading')}</div>
          ) : (
            <div className="space-y-4 mt-4 p-3 sm:p-6">
              <div className="grid gap-3 grid-cols-1 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <label className="text-xs sm:text-sm font-medium">
                    {t('writeDialog.fields.title')}
                  </label>
                  <Input
                    value={docTitle}
                    onChange={(e) => setDocTitle(e.target.value)}
                    placeholder={t('writeDialog.fields.titlePlaceholder')}
                    className="text-xs sm:text-sm"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs sm:text-sm font-medium">
                    {t('writeDialog.fields.description')}
                  </label>
                  <Input
                    value={docDescription}
                    onChange={(e) => setDocDescription(e.target.value)}
                    placeholder={t('writeDialog.fields.descriptionPlaceholder')}
                    className="text-xs sm:text-sm"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs sm:text-sm font-medium">
                  {t('writeDialog.fields.content')}
                </label>
                <RichTextEditor value={docContent} onChange={setDocContent} />
              </div>
              <div className="flex flex-col sm:flex-row justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setWriteOpen(false);
                    resetWriteForm();
                  }}
                  className="w-full sm:w-auto text-xs sm:text-sm"
                >
                  {t('buttons.cancel')}
                </Button>
                <Button
                  onClick={editingDoc ? handleUpdateDoc : handleCreateDoc}
                  disabled={createDocMutation.isPending || updateDocMutation.isPending}
                  className="gap-2 w-full sm:w-auto text-xs sm:text-sm"
                >
                  <HugeiconsIcon icon={File01Icon} className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  {createDocMutation.isPending || updateDocMutation.isPending
                    ? t('writeDialog.saving')
                    : editingDoc
                      ? t('writeDialog.updateDocument')
                      : t('writeDialog.saveDocument')}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{viewingDoc?.title}</DialogTitle>
            {viewingDoc?.description && (
              <DialogDescription>{viewingDoc.description}</DialogDescription>
            )}
          </DialogHeader>
          {viewingDocQuery.isLoading ? (
            <div className="py-6 text-center text-muted-foreground">{t('viewDialog.loading')}</div>
          ) : viewingDocQuery.data ? (
            <div className="mt-4">
              {viewingDocQuery.data.type === 'rich-text' ? (
                <RichTextViewer content={viewingDocQuery.data.content || ''} />
              ) : viewingDocQuery.data.type === 'image' ? (
                <div className="flex justify-center">
                  <img
                    src={getFileUrl(viewingDocQuery.data.r2Key) || ''}
                    alt={viewingDocQuery.data.title}
                    className="max-w-full h-auto rounded-lg"
                    onError={(e) => {
                      console.error('Failed to load image:', viewingDocQuery.data.r2Key);
                      e.currentTarget.src = '/placeholder.svg';
                    }}
                  />
                </div>
              ) : viewingDocQuery.data.type === 'pdf' ? (
                <div className="flex justify-center">
                  <iframe
                    src={getFileUrl(viewingDocQuery.data.r2Key) || ''}
                    className="w-full h-[600px] rounded-lg border"
                    title={viewingDocQuery.data.title}
                  />
                </div>
              ) : null}
            </div>
          ) : viewingDoc ? (
            <div className="mt-4">
              {viewingDoc.type === 'rich-text' ? (
                <RichTextViewer content={viewingDoc.content || ''} />
              ) : viewingDoc.type === 'image' ? (
                <div className="flex justify-center">
                  <img
                    src={getFileUrl(viewingDoc.r2Key) || ''}
                    alt={viewingDoc.title}
                    className="max-w-full h-auto rounded-lg"
                    onError={(e) => {
                      console.error('Failed to load image:', viewingDoc.r2Key);
                      e.currentTarget.src = '/placeholder.svg';
                    }}
                  />
                </div>
              ) : viewingDoc.type === 'pdf' ? (
                <div className="flex justify-center">
                  <iframe
                    src={getFileUrl(viewingDoc.r2Key) || ''}
                    className="w-full h-[600px] rounded-lg border"
                    title={viewingDoc.title}
                  />
                </div>
              ) : null}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('deleteDialog.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('deleteDialog.confirm', { title: docToDelete?.title })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDocToDelete(null)}>
              {t('buttons.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (docToDelete) {
                  deleteDocMutation.mutate(docToDelete.id);
                  setDocToDelete(null);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('buttons.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Move to Folder Dialog */}
      <Dialog open={moveDialogOpen} onOpenChange={setMoveDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('moveDialog.title')}</DialogTitle>
            <DialogDescription>
              {t('moveDialog.description', { title: docToMove?.title })}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {foldersQuery.isLoading ? (
              <div className="flex items-center justify-center py-8">
                <HugeiconsIcon
                  icon={Loading01Icon}
                  className="h-5 w-5 animate-spin text-muted-foreground"
                />
              </div>
            ) : (
              <div className="border rounded-md max-h-[300px] overflow-y-auto">
                <FolderSelector
                  folders={foldersQuery.data ?? []}
                  selectedFolderId={null}
                  onFolderSelect={(folderId) => {
                    if (docToMove) {
                      moveDocMutation.mutate({ id: docToMove.id, targetFolderId: folderId });
                    }
                  }}
                  currentDocFolderId={docToMove?.folderId ?? null}
                  t={t}
                />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Viewer component for rich text content
function RichTextViewer({ content }: { content: string }) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Image.configure({
        inline: false,
        allowBase64: true,
      }),
    ],
    content: (() => {
      if (!content) return '';
      try {
        const json = JSON.parse(content);
        return json;
      } catch {
        return content;
      }
    })(),
    editable: false,
    editorProps: {
      attributes: {
        class:
          'tiptap-editor prose prose-sm max-w-none px-3 py-2 border rounded-md text-black bg-white',
      },
    },
  });

  return (
    <>
      <style>{`
        .tiptap-editor h1,
        .tiptap-editor.prose h1,
        .prose.tiptap-editor h1 {
          font-size: 2rem !important;
          font-weight: 700 !important;
          margin-top: 2rem !important;
          margin-bottom: 1rem !important;
        }
        .tiptap-editor h2,
        .tiptap-editor.prose h2,
        .prose.tiptap-editor h2 {
          font-size: 1.5rem !important;
          font-weight: 600 !important;
          margin-top: 1.5rem !important;
          margin-bottom: 0.75rem !important;
        }
        .tiptap-editor h3,
        .tiptap-editor.prose h3,
        .prose.tiptap-editor h3 {
          font-size: 1.25rem !important;
          font-weight: 600 !important;
          margin-top: 1.25rem !important;
          margin-bottom: 0.625rem !important;
        }
        .tiptap-editor ol,
        .tiptap-editor.prose ol,
        .prose.tiptap-editor ol {
          list-style-type: decimal !important;
          padding-left: 1.5rem !important;
        }
        .tiptap-editor ul,
        .tiptap-editor.prose ul,
        .prose.tiptap-editor ul {
          list-style-type: disc !important;
          padding-left: 1.5rem !important;
        }
        .tiptap-editor li {
          display: list-item !important;
          margin-top: 0 !important;
          margin-bottom: 0 !important;
        }
        .tiptap-editor li > p {
          margin-top: 0 !important;
          margin-bottom: 0 !important;
        }
        .tiptap-editor li + li {
          margin-top: 0.25rem !important;
        }
      `}</style>
      <EditorContent editor={editor} />
    </>
  );
}

export const Route = createFileRoute('/_authenticated/knowledge-center')({
  component: KnowledgeCenter,
  validateSearch: (search: Record<string, unknown>) => ({
    docId: (search.docId as string | undefined) ?? undefined,
  }),
});
